import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { useGetSchoolOverviewQuery } from '../../store/api/schoolAdminApi'
import { useSchoolFilters } from '../../components/school/SchoolLayout'
import DateRangeBar from '../../components/school/DateRangeBar'
import {
  KpiCard, UsageBadge, DemoBanner,
  IconUsers, IconTeacher, IconClass, IconActivity, IconChat,
} from '../../components/school/SchoolUi'

const SchoolOverview: React.FC = () => {
  const { t } = useTranslation('school')
  const { filters } = useSchoolFilters()
  const [trendMetric, setTrendMetric] = useState<'activeStudents' | 'aiSessions' | 'unitsActive' | 'aiInteractions'>('activeStudents')
  const { data, isLoading } = useGetSchoolOverviewQuery(filters)

  const weeks = useMemo(() => data?.trend?.weeks || [], [data])

  if (isLoading) {
    return <div className="text-gray-500 py-12">{t('common.loading')}</div>
  }

  if (data?.empty || !data?.organizationId) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
        {t('overview.noSchool')}
      </div>
    )
  }

  const k = data.kpis
  const unitsActive = k.unitsActive ?? 0
  const totalUnits = k.totalUnits ?? k.unitsCreated ?? 0
  const unitsCreated = k.unitsCreated ?? totalUnits
  const metricLabelKey =
    trendMetric === 'activeStudents' ? 'metricActiveStudents'
      : trendMetric === 'aiSessions' ? 'metricAiSessions'
        : trendMetric === 'aiInteractions' ? 'metricAiMessages'
          : 'metricUnitsActive'

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('overview.title')}</h1>
        <p className="mt-1 text-gray-600">{t('overview.subtitle')}</p>
      </div>

      {data.isDemo ? <DemoBanner label={t('common.demoBanner')} /> : null}

      <DateRangeBar classes={data.filters?.classes || []} />

      {/* Director snapshot — participation first */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <KpiCard
          emphasize
          icon={<IconUsers />}
          label={t('overview.studentsActivePeriod')}
          value={`${k.activeStudentsPct}%`}
          hint={`${k.activeStudents} ${t('common.of')} ${k.totalStudents}`}
        />
        <KpiCard
          emphasize
          icon={<IconTeacher />}
          label={t('overview.teachersActivePeriod')}
          value={`${k.activeTeachersPct}%`}
          hint={`${k.activeTeachers} ${t('common.of')} ${k.totalTeachers}`}
        />
      </div>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{t('overview.adoptionSection')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={<IconUsers />}
            label={t('overview.activeStudents')}
            value={<>{k.activeStudents}<span className="text-lg font-semibold text-gray-400"> / {k.totalStudents}</span></>}
            hint={`${k.activeStudentsPct}%`}
          />
          <KpiCard
            icon={<IconTeacher />}
            label={t('overview.activeTeachers')}
            value={<>{k.activeTeachers}<span className="text-lg font-semibold text-gray-400"> / {k.totalTeachers}</span></>}
            hint={`${k.activeTeachersPct}%`}
          />
          <KpiCard
            icon={<IconClass />}
            label={t('overview.activeClasses')}
            value={<>{k.activeClasses}<span className="text-lg font-semibold text-gray-400"> / {k.totalClasses}</span></>}
            hint={`${k.activeClassesPct}%`}
          />
          <KpiCard
            icon={<IconActivity />}
            label={t('overview.units')}
            value={<>{unitsActive}<span className="text-lg font-semibold text-gray-400"> / {totalUnits || unitsCreated}</span></>}
            hint={t('overview.unitsHint', { created: unitsCreated, active: unitsActive })}
          />
        </div>
      </section>

      {data.quality?.available ? (
        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('overview.behaviorTitle')}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{t('overview.behaviorSubtitle')}</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard emphasize label={t('overview.followUp')} value={`${data.quality.followUpPct}%`} />
            <KpiCard emphasize label={t('overview.sources')} value={`${data.quality.sourcesPct}%`} />
            <KpiCard emphasize label={t('overview.reformulated')} value={`${data.quality.reformulatedPct}%`} />
            <KpiCard emphasize label={t('overview.reflections')} value={`${data.quality.reflectionsPct}%`} />
          </div>
        </section>
      ) : null}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-400">{t('overview.aiSection')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard muted label={t('overview.aiSessions')} value={k.aiSessions} hint={t('overview.aiVolumeHint')} />
          <KpiCard muted icon={<IconChat />} label={t('overview.aiMessages')} value={k.aiInteractions} hint={t('overview.aiVolumeHint')} />
          <KpiCard muted label={t('overview.avgMessages')} value={k.avgInteractionsPerStudent} />
          <KpiCard muted label={t('overview.studentsUsingAi')} value={`${k.studentsUsingAiPct}%`} />
        </div>
      </section>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="text-lg font-semibold text-gray-900">{t('overview.usageTrend')}</h2>
          <select
            value={trendMetric}
            onChange={(e) => setTrendMetric(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="activeStudents">{t('overview.metricActiveStudents')}</option>
            <option value="unitsActive">{t('overview.metricUnitsActive')}</option>
            <option value="aiSessions">{t('overview.metricAiSessions')}</option>
            <option value="aiInteractions">{t('overview.metricAiMessages')}</option>
          </select>
        </div>
        {weeks.length === 0 ? (
          <p className="text-sm text-gray-500 py-12 text-center">{t('overview.emptyTrend')}</p>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weeks}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="weekStart" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey={trendMetric === 'unitsActive' ? 'activitiesCompleted' : trendMetric}
                  stroke="#2563eb"
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                  name={t(`overview.${metricLabelKey}`)}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{t('overview.teacherAdoption')}</h2>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('teachers.columns.teacher')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('teachers.columns.classes')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('teachers.columns.activeStudents')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('teachers.columns.aiSessions')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('teachers.columns.usage')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data.teacherAdoption || []).map((row: any) => {
              const total = row.totalStudents ?? row.activeStudents ?? 0
              const active = row.activeStudents ?? 0
              return (
                <tr key={row.userId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">
                    <Link className="text-blue-600 hover:underline font-medium" to={`/pending/teachers/${row.userId}`}>{row.name}</Link>
                  </td>
                  <td className="px-4 py-3 text-sm">{row.classCount}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {active}<span className="text-gray-400 font-normal"> / {total}</span>
                  </td>
                  <td className="px-4 py-3 text-sm">{row.aiSessions}</td>
                  <td className="px-4 py-3 text-sm">
                    <UsageBadge status={row.usageStatus} label={t(`teachers.usage.${row.usageStatus}`)} />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400 text-center">
        {t('overview.adoptionScore')}: {k.adoptionScore}% — {t('overview.adoptionScoreHelp')}
      </p>
    </div>
  )
}

export default SchoolOverview
